import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, Download, ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import AttendanceHistoryModal from './AttendanceHistoryModal';
import api, { getImageUrl } from '../api';

export default function Attendance() {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({ present: 0, halfDay: 0, absent: 0, active: 0, targetDate: '' });
  const [totalItems, setTotalItems] = useState(0);

  const itemsPerPage = 15;

  useEffect(() => {
    fetchAttendance();
  }, [searchTerm, selectedDate, statusFilter, currentPage]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchTerm,
        start_date: selectedDate,
        end_date: selectedDate,
        status: statusFilter
      };

      const response = await api.get('/attendance', { params });
      const rawData = response.data.data || [];
      const totalCount = response.data.total || 0;
      const todayStats = response.data.today_stats || { present: 0, half_day: 0, absent: 0 };

      setStats({
        present: todayStats.present || 0,
        halfDay: todayStats.half_day || 0,
        absent: todayStats.absent || 0,
        active: todayStats.active || 0,
        targetDate: todayStats.target_date || ''
      });

      const formatTime = (timestamp) => {
        if (!timestamp || timestamp === '--:--' || timestamp === 'Active') return timestamp;
        if (typeof timestamp === 'string' && (timestamp.includes('AM') || timestamp.includes('PM'))) return timestamp;
        
        try {
          const dateObj = new Date(timestamp);
          if (isNaN(dateObj.getTime())) {
            const [h, m] = timestamp.split(':');
            const hh = parseInt(h);
            const mm = m;
            const ampm = hh >= 12 ? 'PM' : 'AM';
            const h12 = hh % 12 || 12;
            return `${h12.toString().padStart(2, '0')}:${mm} ${ampm}`;
          }
          return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        } catch (e) {
          return '--:--';
        }
      };

      const transformed = rawData.map((record) => {
        const userName = record.name || record.user?.name || 'Unknown';
        // Backend already sends formatted clockIn/clockOut in h:i A format
        // If clockOut is '--:--', keep it as is. Never override with 'Active' here.
        const clockInVal = record.clockIn || (record.clock_in ? formatTime(record.clock_in) : '--:--');
        const clockOutVal = record.clockOut || (record.clock_out ? formatTime(record.clock_out) : '--:--');
        const workHrsVal = record.workHours || record.work_hours || '--';
        return {
          ...record,
          id: record.id || record.user?.employee?.employee_id || `EMP-${record.user_id}`,
          name: userName,
          designation: record.designation || record.user?.employee?.designation || 'N/A',
          date: record.date || 'N/A',
          clockIn: clockInVal,
          clockOut: clockOutVal,
          status: record.status || 'No Record',
          workHours: workHrsVal,
          remarks: record.remarks || '--',
          avatar: record.avatar || getImageUrl(record.user?.profile_photo_path) || null,
          initial: record.initial || userName.charAt(0).toUpperCase()
        };
      });

      // Stats should ideally come from backend separately, but we'll fetch them from dashboard stats for now
      // Or we can just show the total from the filtered result count if it matches today
      setAttendance(transformed);
      setTotalItems(totalCount);
    } catch (err) {
      console.error("Error fetching attendance:", err);
      setError('Failed to load attendance records.');
    } finally {
      setLoading(false);
    }
  };

  // handleExport removed as per user request

  const handleCardClick = (status) => {
    if (statusFilter === status) {
      setStatusFilter('');
    } else {
      setStatusFilter(status);
    }
    setCurrentPage(1);
  };

  const filtered = attendance; // Backend already handles search/filter
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentData = attendance;

  // Removed early return for loading to keep component mounted during search
  if (error) return <div className="p-20 text-center text-red-500 font-bold">{error}</div>;

  return (
    <div className="max-w-[1400px] mx-auto space-y-4">

      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">Attendance Records</h2>
        </div>

        <div className="flex items-center gap-3">
          <div onClick={() => handleCardClick('Active')} className={`bg-white border text-center rounded-md px-4 py-2 shadow-sm min-w-[110px] cursor-pointer transition-all hover:scale-105 active:scale-95 ${statusFilter === 'Active' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-500'}`}>
            <p className="text-[10px] uppercase font-bold text-gray-400">Active {selectedDate ? `on ${selectedDate}` : 'Today'}</p>
            <p className="text-xl font-black text-blue-500 leading-none mt-1">{stats.active}</p>
          </div>
          <div onClick={() => handleCardClick('Present')} className={`bg-white border text-center rounded-md px-4 py-2 shadow-sm min-w-[110px] cursor-pointer transition-all hover:scale-105 active:scale-95 ${statusFilter === 'Present' ? 'border-[#10b981] ring-2 ring-[#10b981] bg-emerald-50' : 'border-gray-200 hover:border-[#10b981]'}`}>
            <p className="text-[10px] uppercase font-bold text-gray-400">Present {selectedDate ? `on ${selectedDate}` : 'Today'}</p>
            <p className="text-xl font-black text-[#10b981] leading-none mt-1">{stats.present}</p>
          </div>
          <div onClick={() => handleCardClick('Half Day')} className={`bg-white border text-center rounded-md px-4 py-2 shadow-sm min-w-[110px] cursor-pointer transition-all hover:scale-105 active:scale-95 ${statusFilter === 'Half Day' ? 'border-orange-500 ring-2 ring-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-500'}`}>
            <p className="text-[10px] uppercase font-bold text-gray-400">Half Day {selectedDate ? `on ${selectedDate}` : 'Today'}</p>
            <p className="text-xl font-black text-orange-500 leading-none mt-1">{stats.halfDay}</p>
          </div>
          <div onClick={() => handleCardClick('Absent')} className={`bg-white border text-center rounded-md px-4 py-2 shadow-sm min-w-[110px] cursor-pointer transition-all hover:scale-105 active:scale-95 ${statusFilter === 'Absent' ? 'border-red-500 ring-2 ring-red-500 bg-red-50' : 'border-gray-200 hover:border-red-500'}`}>
            <p className="text-[10px] uppercase font-bold text-gray-400">Absent {selectedDate ? `on ${selectedDate}` : 'Today'}</p>
            <p className="text-xl font-black text-red-500 leading-none mt-1">{stats.absent}</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Search employee..."
            className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 w-full transition-all"
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="flex items-center border border-gray-200 rounded-md bg-white px-3 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-[#10b981]/20">
            <input
              type="date"
              title="Select Date"
              value={selectedDate}
              onChange={(e) => { setSelectedDate(e.target.value); setCurrentPage(1); }}
              className="text-xs font-bold text-emerald-600 focus:outline-none bg-transparent cursor-pointer"
            />
            {selectedDate && (
              <X size={14} className="ml-2 text-slate-400 cursor-pointer hover:text-red-500 shrink-0" onClick={() => { setSelectedDate(''); setCurrentPage(1); }} />
            )}
          </div>

          {statusFilter && (
            <button onClick={() => { setStatusFilter(''); }} className="text-xs font-bold text-red-500 hover:bg-red-50 px-2.5 py-2 rounded-md transition-colors border border-red-100 flex items-center gap-1 shadow-sm whitespace-nowrap">
              <X size={12} strokeWidth={3} /> Clear {statusFilter} Filter
            </button>
          )}

          {/* Export button removed as per user request */}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/50 z-20 flex items-center justify-center backdrop-blur-[1px]">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="animate-spin text-emerald-500" size={32} />
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Updating...</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white border-b border-gray-100 text-[11px] uppercase font-black text-slate-500 tracking-wider whitespace-nowrap">
                <th className="px-6 py-4">EMPLOYEE</th>
                <th className="px-4 py-4">DESIGNATION</th>
                <th className="px-4 py-4">DATE</th>
                <th className="px-4 py-4">CLOCK IN</th>
                <th className="px-4 py-4">CLOCK OUT</th>
                <th className="px-4 py-4">STATUS</th>
                <th className="px-4 py-4">WORK HOURS</th>
                <th className="px-6 py-4">REMARKS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {currentData.map((record, i) => (
                <tr
                  key={i}
                  className="hover:bg-gray-50/50 transition-colors cursor-pointer group"
                  onClick={() => setSelectedEmployee(record)}
                >
                  <td className="px-6 py-3 min-w-[200px]">
                    <div className="flex items-center gap-3">
                      {record.avatar ? (
                        <img src={record.avatar} alt={record.name} className="w-8 h-8 rounded-full object-cover border border-gray-200" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold font-sans uppercase">
                          {record.initial}
                        </div>
                      )}
                      <div>
                        <p className="text-[13px] font-bold text-slate-800 capitalize leading-tight">{record.name}</p>
                        <p className="text-[10px] font-semibold text-gray-400 mt-0.5">ID: {record.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 min-w-[150px]">
                    <span className="text-xs font-semibold text-slate-600">{record.designation}</span>
                  </td>
                  <td className="px-4 py-3 min-w-[120px]">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                      <Calendar size={12} className="text-gray-400" />
                      {record.date}
                    </div>
                  </td>
                  <td className="px-4 py-3 min-w-[110px]">
                    <div className={`flex items-center gap-1.5 text-xs font-bold ${record.clockIn !== '--:--' ? 'text-emerald-500' : 'text-gray-400'}`}>
                      <Clock size={12} className={record.clockIn !== '--:--' ? 'text-emerald-400' : 'text-gray-300'} />
                      {record.clockIn}
                    </div>
                  </td>
                  <td className="px-4 py-3 min-w-[110px]">
                    <div className={`flex items-center gap-1.5 text-xs font-bold ${record.clockOut === 'Active' ? 'text-blue-500' : 'text-gray-400'}`}>
                      {record.clockOut !== '--:--' && record.clockOut !== 'Active' && <Clock size={12} className="text-gray-300" />}
                      <span className={record.clockOut === 'Active' ? 'px-2 py-0.5 bg-blue-50 rounded-md border border-blue-100' : ''}>{record.clockOut}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 min-w-[100px]">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${record.status === 'Active' ? 'text-blue-600 bg-blue-50 border-blue-100' :
                        record.status === 'Present' ? 'text-[#10b981] bg-emerald-50 border-emerald-100' :
                          record.status === 'Half Day' ? 'text-orange-500 bg-orange-50 border-orange-100' :
                            record.status === 'Absent' ? 'text-red-500 bg-red-50 border-red-100' :
                              'text-gray-500 bg-gray-50 border-gray-200'}`}>
                      {record.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 min-w-[100px]">
                    {record.workHours !== '--' ? (
                      <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-md">{record.workHours} hrs</span>
                    ) : (
                      <span className="text-[12px] font-bold text-gray-300">{record.workHours}</span>
                    )}
                  </td>
                  <td className="px-6 py-3 min-w-[180px]">
                    <span className="text-xs font-medium text-slate-500">{record.remarks}</span>
                  </td>
                </tr>
              ))}
              {currentData.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm font-semibold text-slate-400">No attendance records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm font-semibold text-slate-500">
          Showing page {currentPage} of {totalPages}
        </p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-50 transition-colors border border-transparent shadow-none"
          >
            Previous
          </button>

          {totalPages > 1 && Array.from({ length: totalPages }).map((_, idx) => {
            const pageNum = idx + 1;
            if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 flex items-center justify-center rounded-md text-xs font-bold transition-all shadow-sm ${currentPage === pageNum
                      ? 'bg-[#10b981] text-white border border-[#10b981]'
                      : 'text-gray-500 hover:bg-gray-100 border border-transparent hover:border-gray-200 bg-white'
                    }`}
                >{pageNum}</button>
              );
            }
            if ((pageNum === 2 && currentPage > 3) || (pageNum === totalPages - 1 && currentPage < totalPages - 2)) {
              return <span key={pageNum} className="text-gray-400 px-1 font-bold">...</span>;
            }
            return null;
          })}

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 text-sm font-bold text-slate-600 hover:bg-gray-100 rounded-md disabled:opacity-50 transition-colors border border-transparent shadow-none"
          >
            Next
          </button>
        </div>
      </div>

      <AttendanceHistoryModal
        isOpen={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
      />
    </div>
  );
}
